/**
 * AbilityIcon - 64×64 HUD ability slot with icon, hotkey chip, and radial
 * cooldown overlay driven by a conic-gradient CSS variable.
 */

import { BaseUIComponent } from './BaseUIComponent.js';
import { Keystroke } from './Keystroke.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export interface AbilityIconOptions extends BaseUIComponentOptions {
  /** SVG markup shown in the icon art slot. */
  iconHtml?: string;
  /** Keyboard shortcut label shown in the bottom-right chip. */
  hotkey?: string;
  /** Remaining cooldown in seconds. 0 = ready. */
  cooldown?: number;
  /** Whether the ability is usable (not on cooldown / not disabled). */
  ready?: boolean;
}

export class AbilityIcon extends BaseUIComponent<AbilityIconOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Ability Icon',
    category: 'hud',
    summary: '64×64 HUD ability slot with icon, hotkey chip, and radial cooldown overlay.',
    useCases: ['ability', 'skill', 'spell', 'hotbar', 'ability slot', 'cooldown'],
    optionsType: 'AbilityIconOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/ability-icon.html',
      styles: '@engine/assets/ui/styles/ability-icon.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private artElement: HTMLElement | null = null;
  private hotkeySlot: HTMLElement | null = null;
  private hotkeyChip: Keystroke | null = null;
  private cooldownElement: HTMLElement | null = null;
  private cooldownTextElement: HTMLElement | null = null;
  private cooldownTimer: ReturnType<typeof setInterval> | null = null;
  private cooldownRemaining = 0;
  private cooldownTotal = 0;

  protected override getAssetPaths() {
    return {
      templatePath: AbilityIcon.metadata.assetPaths.template,
      stylesPath: AbilityIcon.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<AbilityIconOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      iconHtml: '',
      hotkey: '',
      cooldown: 0,
      ready: true,
    };
  }

  protected override getInitialData(): Record<string, any> {
    return { hotkey: this.options.hotkey };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-ability-icon') as HTMLElement | null;
    this.artElement = this.layout.querySelector('[data-ability-icon-art]') as HTMLElement | null;
    this.hotkeySlot = this.layout.querySelector('[data-ability-icon-hotkey]') as HTMLElement | null;
    this.cooldownElement = this.layout.querySelector('[data-ability-icon-cooldown]') as HTMLElement | null;
    this.cooldownTextElement = this.layout.querySelector('[data-ability-icon-cooldown-text]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    if (this.hotkeySlot) {
      this.hotkeyChip = await this.mountChild(
        Keystroke,
        { key: this.options.hotkey, size: 'small' },
        this.hotkeySlot,
      );
      this.applyHotkeyVisibility();
    }
    if (this.options.iconHtml) this.setIconHtml(this.options.iconHtml);
    this.applyReady(this.options.ready);
    if (this.options.cooldown > 0) this.setCooldown(this.options.cooldown);
  }

  /** Inject SVG markup into the art slot. */
  public setIconHtml(svg: string): void {
    this.options.iconHtml = svg;
    if (this.artElement) this.artElement.innerHTML = svg;
  }

  /** Update the hotkey chip label. */
  public setHotkey(key: string): void {
    this.options.hotkey = key;
    this.hotkeyChip?.setKey(key);
    this.applyHotkeyVisibility();
  }

  private applyHotkeyVisibility(): void {
    if (!this.hotkeySlot) return;
    this.hotkeySlot.style.display = this.options.hotkey ? '' : 'none';
  }

  /** Start a cooldown countdown in seconds. */
  public setCooldown(seconds: number): void {
    this.stopCooldownTimer();
    this.options.cooldown = seconds;
    this.cooldownTotal = seconds;
    this.cooldownRemaining = seconds;
    if (seconds <= 0) {
      this.applyCooldownProgress(0);
      return;
    }
    this.applyReady(false);
    this.applyCooldownProgress(1);
    this.cooldownTimer = setInterval(() => {
      this.cooldownRemaining -= 0.1;
      if (this.cooldownRemaining <= 0) {
        this.cooldownRemaining = 0;
        this.stopCooldownTimer();
        this.applyCooldownProgress(0);
        this.applyReady(true);
      } else {
        this.applyCooldownProgress(this.cooldownRemaining / this.cooldownTotal);
      }
    }, 100);
  }

  /** Manually mark the ability as ready or not-ready (no cooldown animation). */
  public setReady(ready: boolean): void {
    this.options.ready = ready;
    this.applyReady(ready);
  }

  private applyReady(ready: boolean): void {
    if (!this.rootElement) return;
    this.rootElement.dataset.ready = ready ? 'true' : 'false';
    this.rootElement.dataset.onCooldown = ready ? 'false' : 'true';
  }

  private applyCooldownProgress(fraction: number): void {
    if (!this.rootElement || !this.cooldownElement || !this.cooldownTextElement) return;
    const deg = fraction * 360;
    this.cooldownElement.style.setProperty('--ui-ability-cd-deg', `${deg}deg`);
    this.rootElement.dataset.onCooldown = fraction > 0 ? 'true' : 'false';
    const secs = Math.ceil(this.cooldownRemaining);
    this.cooldownTextElement.textContent = fraction > 0 ? String(secs) : '';
  }

  private stopCooldownTimer(): void {
    if (this.cooldownTimer !== null) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  }

  protected override onDestroy(): void {
    this.stopCooldownTimer();
    // hotkeyChip is destroyed by BaseUIComponent.destroy() via mountChild tracking.
    this.hotkeyChip = null;
    this.rootElement = null;
    this.artElement = null;
    this.hotkeySlot = null;
    this.cooldownElement = null;
    this.cooldownTextElement = null;
  }
}
